import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, TrendingUp, MapPin, ChevronRight, Flame } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroAccent } from "@/components/shared/PageHero";
import { AdSense, LeaderboardAd } from "@/components/shared/AdSense";
import AdFrame from "@/components/shared/AdFrame";
import Header from "@/components/layout/Header";
import { getIndustryIcon } from "@/lib/industryIcons";

const IndustrySelector = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState<string>('united-states');

  // Industry mapping
  const industries = [
    { code: '11', title: 'Agriculture, Forestry, Fishing and Hunting', description: 'Primary agricultural production and related services' },
    { code: '21', title: 'Mining, Quarrying, Oil and Gas Extraction', description: 'Extraction of natural resources including oil, gas, and minerals' },
    { code: '22', title: 'Utilities', description: 'Electric power, natural gas, water, and waste management services' },
    { code: '23', title: 'Construction', description: 'Building construction, infrastructure, and specialty trade contractors' },
    { code: '31', title: 'Manufacturing', description: 'Production of goods from raw materials and components' },
    { code: '42', title: 'Wholesale Trade', description: 'Distribution of goods to retailers, businesses, and institutions' },
    { code: '44', title: 'Retail Trade', description: 'Direct sales of goods to consumers through stores and online' },
    { code: '48', title: 'Transportation and Warehousing', description: 'Moving people and goods, storage and logistics services' },
    { code: '51', title: 'Information', description: 'Publishing, broadcasting, telecommunications, and data processing' },
    { code: '52', title: 'Finance and Insurance', description: 'Banking, investment, insurance, and financial services' },
    { code: '53', title: 'Real Estate and Rental', description: 'Property development, management, and equipment rental' },
    { code: '54', title: 'Professional, Scientific, and Technical Services', description: 'Specialized professional services and consulting' },
    { code: '55', title: 'Management of Companies', description: 'Corporate headquarters and holding companies' },
    { code: '56', title: 'Administrative and Support Services', description: 'Business support services and facilities management' },
    { code: '61', title: 'Educational Services', description: 'Schools, colleges, universities, and training institutions' },
    { code: '62', title: 'Health Care and Social Assistance', description: 'Medical services, hospitals, and social care programs' },
    { code: '71', title: 'Arts, Entertainment, and Recreation', description: 'Entertainment venues, sports, arts, and recreational facilities' },
    { code: '72', title: 'Accommodation and Food Services', description: 'Hotels, restaurants, and food service establishments' },
    { code: '81', title: 'Other Services', description: 'Personal services, repairs, and miscellaneous professional services' },
    { code: '92', title: 'Public Administration', description: 'Government agencies and public sector organizations' }
  ];

  // Industries with documented labor shortages (based on BLS JOLTS + projections data)
  const industryShortageMap: Record<string, { level: 'critical' | 'high' | 'moderate'; label: string }> = {
    '23': { level: 'critical', label: 'Worker Shortage' },
    '48': { level: 'critical', label: 'Driver Shortage' },
    '62': { level: 'critical', label: 'Staff Shortage' },
    '31': { level: 'high', label: 'Skilled Workers Needed' },
    '72': { level: 'high', label: 'Hiring Demand' },
    '54': { level: 'moderate', label: 'Growing Demand' },
    '22': { level: 'moderate', label: 'Aging Workforce' },
  };

  const locations = [
    { value: 'united-states', label: 'United States' },
    { value: 'alabama', label: 'Alabama' },
    { value: 'alaska', label: 'Alaska' },
    { value: 'arizona', label: 'Arizona' },
    { value: 'arkansas', label: 'Arkansas' },
    { value: 'california', label: 'California' },
    { value: 'colorado', label: 'Colorado' },
    { value: 'connecticut', label: 'Connecticut' },
    { value: 'delaware', label: 'Delaware' },
    { value: 'florida', label: 'Florida' },
    { value: 'georgia', label: 'Georgia' },
    { value: 'hawaii', label: 'Hawaii' },
    { value: 'idaho', label: 'Idaho' },
    { value: 'illinois', label: 'Illinois' },
    { value: 'indiana', label: 'Indiana' },
    { value: 'iowa', label: 'Iowa' },
    { value: 'kansas', label: 'Kansas' },
    { value: 'kentucky', label: 'Kentucky' },
    { value: 'louisiana', label: 'Louisiana' },
    { value: 'maine', label: 'Maine' },
    { value: 'maryland', label: 'Maryland' },
    { value: 'massachusetts', label: 'Massachusetts' },
    { value: 'michigan', label: 'Michigan' },
    { value: 'minnesota', label: 'Minnesota' },
    { value: 'mississippi', label: 'Mississippi' },
    { value: 'missouri', label: 'Missouri' },
    { value: 'montana', label: 'Montana' },
    { value: 'nebraska', label: 'Nebraska' },
    { value: 'nevada', label: 'Nevada' },
    { value: 'new-hampshire', label: 'New Hampshire' },
    { value: 'new-jersey', label: 'New Jersey' },
    { value: 'new-mexico', label: 'New Mexico' },
    { value: 'new-york', label: 'New York' },
    { value: 'north-carolina', label: 'North Carolina' },
    { value: 'north-dakota', label: 'North Dakota' },
    { value: 'ohio', label: 'Ohio' },
    { value: 'oklahoma', label: 'Oklahoma' },
    { value: 'oregon', label: 'Oregon' },
    { value: 'pennsylvania', label: 'Pennsylvania' },
    { value: 'rhode-island', label: 'Rhode Island' },
    { value: 'south-carolina', label: 'South Carolina' },
    { value: 'south-dakota', label: 'South Dakota' },
    { value: 'tennessee', label: 'Tennessee' },
    { value: 'texas', label: 'Texas' },
    { value: 'utah', label: 'Utah' },
    { value: 'vermont', label: 'Vermont' },
    { value: 'virginia', label: 'Virginia' },
    { value: 'washington', label: 'Washington' },
    { value: 'west-virginia', label: 'West Virginia' },
    { value: 'wisconsin', label: 'Wisconsin' },
    { value: 'wyoming', label: 'Wyoming' }
  ];

  const handleIndustrySelect = (industryCode: string) => {
    navigate(`/industry/${industryCode}/${selectedLocation}`);
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <Header />
      <SeoHead
        title="Industry Salary Analysis - Explore Career Opportunities by Sector"
        description="Comprehensive salary analysis across all major industry sectors. Compare wages, job growth, and career opportunities in different industries nationwide."
      />

      {/* Hero Section */}
      <PageHero
        badge={{ label: 'Industry Analysis Center' }}
        title={
          <>
            Explore Industries &
            <HeroAccent> Career Opportunities</HeroAccent>
          </>
        }
        subtitle="Discover salary trends, job growth, and career paths across all major industry sectors"
      >
        {/* Location Selector */}
        <div className="max-w-md mx-auto mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="h-5 w-5" />
            <span className="text-lg font-medium text-white">Select Location:</span>
          </div>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg z-50">
              {locations.map((location) => (
                <SelectItem key={location.value} value={location.value}>
                  {location.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <LeaderboardAd className="max-w-4xl" />
        </AdFrame>
      </PageHero>

      {/* Industries Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Choose an Industry Sector</h2>
            <p className="text-xl text-muted-foreground">
              Click on any industry to explore detailed salary data and career opportunities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry) => {
              const shortage = industryShortageMap[industry.code];
              const IndustryIcon = getIndustryIcon(industry.code);
              return (
              <Card 
                key={industry.code}
                className="hover:shadow-lg transition-all duration-300 cursor-pointer border-border/40 hover:border-primary/20"
                onClick={() => handleIndustrySelect(industry.code)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <IndustryIcon className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden />
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            NAICS {industry.code}
                          </Badge>
                          {shortage && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                shortage.level === 'critical'
                                  ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                                  : shortage.level === 'high'
                                  ? 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800'
                                  : 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                              }`}
                            >
                              <Flame className="h-3 w-3 mr-1" />
                              {shortage.label}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg leading-tight">
                          {industry.title}
                        </CardTitle>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {industry.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>Jobs</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      <span>Trends</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      <span>Analysis</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustrySelector;